import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Anchor, ArrowLeft, History, ChevronRight, Calendar } from "lucide-react";

const FUNCTION_LABELS = { 1: "Navigation", 2: "Cargo Handling", 3: "Ship Ops & Safety" };

export default function SessionHistoryPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sessions/history");
        setSessions(data);
      } catch { /* ignore */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="cop-glass border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-30" data-testid="history-header">
        <Button variant="ghost" size="sm" className="p-1" onClick={() => navigate("/dashboard")} data-testid="history-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Anchor className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Session History</span>
      </header>

      <main className="container mx-auto px-4 max-w-lg py-6" data-testid="history-main">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12">
            <History className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm mb-4">No sessions yet. Start your first practice!</p>
            <Button size="sm" asChild><Link to="/dashboard">Go to Dashboard</Link></Button>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((s) => (
              <Card
                key={s.id}
                className="border cursor-pointer hover:shadow-sm transition-shadow"
                onClick={() => s.is_active ? navigate(`/session/${s.id}`) : navigate(`/session/${s.id}/results`)}
                data-testid={`history-session-${s.id}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">{FUNCTION_LABELS[s.function_number] || `Function ${s.function_number}`}</span>
                      {s.is_active ? (
                        <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">In Progress</Badge>
                      ) : (
                        <Badge variant={s.result === "pass" ? "default" : "destructive"} className="text-xs">
                          {s.score}/10 {s.result === "pass" ? "Pass" : "Fail"}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {formatDate(s.started_at)}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
